import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Modal } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { format, parse } from "date-fns";
import colors from "../../assets/colors";
import {
  ZoomCall,
  ZoomCallType,
  ZoomCallStatusType,
  MutationCreateUpdateZoomCallArgs,
  Client,
  UserType,
} from "../../types/gqlTypes";
import { useKeyPress } from "../../utils/customHooks";
import ButtonTag from "../ButtonTag";
import Dropdown from "../Dropdown";
import "react-datepicker/dist/react-datepicker.css";
import { createUpdateZoomCall } from "../../redux/actions/ClientDetail";
import { StoreState } from "../../redux/reducers";
import { getAppConfig } from "../../config";

export const zoomCallTypeLabels: Record<ZoomCallType, string> = {
  [ZoomCallType.Intro]: "Intro",
  [ZoomCallType.Strategy]: "Strategy",
  [ZoomCallType.LiveTraining]: "Live Training",
};

export const zoomCallStatusLabels: Record<ZoomCallStatusType, string> = {
  [ZoomCallStatusType.Scheduled]: "Scheduled",
  [ZoomCallStatusType.Completed]: "Completed",
  [ZoomCallStatusType.Incomplete]: "Incomplete",
  [ZoomCallStatusType.NoShow]: "No Show",
};

// Whether a saved zoom call used one of the client's live training sessions: a Live Training call
// that was Completed or No Show. Taking it out of that, or deleting it, gives the session back and
// removes the coach's credit for it.
export const usedLiveTrainingSession = (zoomCall: ZoomCall): boolean =>
  zoomCall?.type === ZoomCallType.LiveTraining &&
  (zoomCall?.status === ZoomCallStatusType.Completed ||
    zoomCall?.status === ZoomCallStatusType.NoShow);

// What giving a used session back does, for the delete confirmation and the edit dialog's note.
// A coach is told it's their credit.
export const givesSessionBackText = (isTrainer: boolean): string =>
  `gives the client's live training session back and removes ${
    isTrainer ? "your" : "the coach's"
  } credit for it.`;

interface Props {
  show?: boolean;
  confirmButtonText?: string;
  cancelButtonText?: string;
  confirmButtonColor?: string;
  cancelButtonColor?: string;
  closeOnDimmerClick?: boolean;
  onCancel?: () => void;
  zoomCall: ZoomCall;
  dispatchCreateUpdateZoomCall: (MutationCreateUpdateZoomCallArgs) => ZoomCall;
  addMode: boolean;
  editMode: boolean;
  client: Client;
  liveTrainingRemainingSessions?: number;
  isTrainer: boolean;
}

interface DateInputFieldProps {
  value?: string;
  onClick?: (date?: any) => void;
}

const EditDialog = (props: Props) => {
  const {
    show,
    confirmButtonText,
    cancelButtonText,
    confirmButtonColor,
    cancelButtonColor,
    closeOnDimmerClick,
    onCancel,
    zoomCall,
    dispatchCreateUpdateZoomCall,
    editMode,
    client,
    liveTrainingRemainingSessions,
    isTrainer,
  } = props;

  useKeyPress("Enter", () => {
    onConfirm?.();
  });
  useKeyPress("Escape", () => {
    onCancel?.();
  });

  const dateFormatMask = "MM.dd.yyyy";
  const fieldWidth = 180;
  const [localStatus, setLocalStatus] = useState(
    zoomCall?.status || ZoomCallStatusType.Scheduled
  );
  const [localType, setLocalType] = useState(
    zoomCall?.type || ZoomCallType.Strategy
  );
  const parsedDate = zoomCall?.date
    ? parse(zoomCall.date?.slice(0, 10) || null, "yyyy-MM-dd", new Date())
    : new Date();
  const [localDate, setLocalDate] = useState(
    format(parsedDate, dateFormatMask) || null
  );
  const [localId] = useState(zoomCall?.id || null);

  useEffect(() => {
    setLocalStatus(zoomCall?.status);
    setLocalType(zoomCall?.type);
  }, [zoomCall]);

  const zoomCallTypeOptions = [
    ZoomCallType.Intro,
    ZoomCallType.Strategy,
    ...(getAppConfig().liveTrainingSessionsEnabled
      ? [ZoomCallType.LiveTraining]
      : []),
  ].map((type) => ({ text: zoomCallTypeLabels[type], value: type }));

  // A Live Training session can only be marked Completed or No Show on or after its date, and
  // only while the client has a session left; the API rejects both. A call that already used a
  // session keeps the options, since re-saving it never takes another. The picked date is local
  // midnight, as it's sent, so today's date is never in the future.
  const isLiveTraining =
    getAppConfig().liveTrainingSessionsEnabled &&
    localType === ZoomCallType.LiveTraining;
  const alreadyUsedSession = usedLiveTrainingSession(zoomCall);
  let sessionUnavailableReason: string = null;
  if (isLiveTraining && !alreadyUsedSession) {
    if (parse(localDate, dateFormatMask, new Date()) > new Date()) {
      sessionUnavailableReason =
        "Completed and No Show become available on the session's date.";
    } else if (liveTrainingRemainingSessions === 0) {
      sessionUnavailableReason =
        "This client has no live training sessions left.";
    }
  }

  // Saving a call that used a session as anything but a Completed or No Show Live Training call
  // gives the session back (the API undoes it), so say so before Save
  const givesSessionBack =
    alreadyUsedSession &&
    !(
      localType === ZoomCallType.LiveTraining &&
      (localStatus === ZoomCallStatusType.Completed ||
        localStatus === ZoomCallStatusType.NoShow)
    );
  const statusNote =
    sessionUnavailableReason ||
    (givesSessionBack
      ? `Saving this ${givesSessionBackText(isTrainer)}`
      : null);

  // Fall back to Scheduled when Completed or No Show stops being offered, e.g. after picking a
  // future date
  useEffect(() => {
    if (
      sessionUnavailableReason &&
      (localStatus === ZoomCallStatusType.Completed ||
        localStatus === ZoomCallStatusType.NoShow)
    ) {
      setLocalStatus(ZoomCallStatusType.Scheduled);
    }
  }, [sessionUnavailableReason, localStatus]);

  // No Show (the client didn't attend) is for Live Training only. It uses a session and credits
  // the coach exactly like Completed.
  const zoomCallStatusTypeOptions = [
    ZoomCallStatusType.Scheduled,
    ...(sessionUnavailableReason ? [] : [ZoomCallStatusType.Completed]),
    ...(isLiveTraining && !sessionUnavailableReason
      ? [ZoomCallStatusType.NoShow]
      : []),
  ].map((status) => ({ text: zoomCallStatusLabels[status], value: status }));

  const DateInputField = (props: DateInputFieldProps) => {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={(a: any) => {
          if (props?.onClick) {
            props.onClick();
          }
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" && props?.onClick) {
            props?.onClick();
          }
        }}
        className="d-flex flex-column justify-content-center align-items-center medium-bold pointer"
        style={{
          backgroundColor: colors.caliber_gray_bg,
          color: colors.caliber_gray_text,
          padding: "0px 35px 0px 20px",
          borderRadius: 8,
          width: fieldWidth,
          margin: 0,
          fontSize: "18px",
          height: "36px",
        }}
      >
        {localDate || "--"}
      </div>
    );
  };

  const onConfirm = () => {
    dispatchCreateUpdateZoomCall({
      clientId: client.id,
      status: localStatus,
      date: parse(localDate, dateFormatMask, new Date()),
      zoomCallId: localId,
      zoomCallType: localType,
    });
  };

  return (
    <Modal
      centered
      show={show}
      onHide={closeOnDimmerClick ? onCancel : () => {}}
    >
      <div
        className="d-flex flex-column"
        style={{ padding: "25px 15px 15px 15px", minHeight: 100 }}
      >
        {show && (
          <div
            className="d-flex flex-column justify-content-center align-items-center paragraph-large"
            style={{
              flex: 1,
              marginTop: 3,
              marginBottom: 3,
              textAlign: "center",
              whiteSpace: "pre-line",
              color: colors.caliber_secondary,
              lineHeight: "24px",
              fontSize: "18px",
            }}
          >
            {editMode ? "Edit Zoom Call" : "Add Zoom Call"}
            <div
              className="col-16 flex-grow-1"
              style={{
                marginTop: 25,
              }}
            >
              <div
                className="d-flex justify-content-between align-items-center"
                style={{
                  margin: "20px 9px 0px 13px",
                }}
              >
                <div
                  className="medium-bold"
                  style={{
                    fontSize: "12px",
                    lineHeight: "16px",
                  }}
                >
                  Type
                </div>

                <div
                  className="medium-bold"
                  style={{
                    padding: "1px 10px",
                    borderRadius: 4,
                    maxWidth: 200,
                  }}
                >
                  <Dropdown
                    height={36}
                    width={fieldWidth}
                    value={localType}
                    items={zoomCallTypeOptions}
                    onSelect={(value) => {
                      setLocalType(value);
                      // No Show isn't offered on other types; fall back to Scheduled rather than
                      // guess Completed, which would use a strategy call
                      if (
                        value !== ZoomCallType.LiveTraining &&
                        localStatus === ZoomCallStatusType.NoShow
                      ) {
                        setLocalStatus(ZoomCallStatusType.Scheduled);
                      }
                    }}
                    textColor={colors.caliber_gray_text}
                    bgColor={colors.caliber_light_gray}
                    borderColor={colors.caliber_light_gray}
                  />
                </div>
              </div>

              <div
                className="d-flex justify-content-between align-items-center"
                style={{
                  margin: "20px 9px 0px 13px",
                }}
              >
                <div
                  className="medium-bold"
                  style={{
                    fontSize: "12px",
                    lineHeight: "16px",
                  }}
                >
                  Date
                </div>

                <div
                  className="medium-bold"
                  style={{
                    padding: "1px 10px",
                    borderRadius: 4,
                    maxWidth: 200,
                  }}
                >
                  <DatePicker
                    onChange={(date) => {
                      setLocalDate(format(date, dateFormatMask));
                    }}
                    customInput={<DateInputField />}
                    selected={parsedDate}
                  />
                </div>
              </div>

              <div
                className="d-flex justify-content-between align-items-center"
                style={{
                  margin: "20px 9px 0px 13px",
                }}
              >
                <div
                  className="medium-bold"
                  style={{
                    fontSize: "12px",
                    lineHeight: "16px",
                  }}
                >
                  Status
                </div>

                <div
                  className="medium-bold"
                  style={{
                    padding: "1px 10px",
                    borderRadius: 4,
                    maxWidth: 200,
                  }}
                >
                  <Dropdown
                    height={36}
                    width={fieldWidth}
                    value={localStatus}
                    items={zoomCallStatusTypeOptions}
                    onSelect={(value) => setLocalStatus(value)}
                    textColor={colors.caliber_gray_text}
                    bgColor={colors.caliber_light_gray}
                    borderColor={colors.caliber_light_gray}
                  />
                </div>
              </div>
              {statusNote && (
                // Zero width with a 100% minimum wraps the note to the fields' width instead of
                // widening the dialog
                <div
                  style={{
                    width: 0,
                    minWidth: "100%",
                    boxSizing: "border-box",
                    padding: "10px 9px 0px 13px",
                    fontSize: "12px",
                    lineHeight: "16px",
                    color: colors.caliber_gray_text,
                  }}
                >
                  {statusNote}
                </div>
              )}
            </div>
          </div>
        )}
        <div
          className="d-flex justify-content-center"
          style={{
            height: 62,
          }}
        >
          {onCancel && (
            <ButtonTag
              margin={6}
              text={cancelButtonText || "Leave"}
              onClick={({ event }) => {
                event.stopPropagation();
                onCancel();
              }}
              height={24}
              textColor={colors.caliber_white}
              bgColor={cancelButtonColor || colors.caliber_gray_text}
              minWidth={91}
            />
          )}
          <ButtonTag
            margin={6}
            text={confirmButtonText || "Delete"}
            onClick={({ event }) => {
              event.stopPropagation();
              onConfirm();
              onCancel();
            }}
            height={24}
            textColor={colors.caliber_white}
            bgColor={confirmButtonColor || colors.caliber_green_200}
            minWidth={91}
          />
        </div>
      </div>
    </Modal>
  );
};

const mapDispatchToProps = (dispatch) => ({
  dispatchCreateUpdateZoomCall: (args: MutationCreateUpdateZoomCallArgs) => {
    dispatch(createUpdateZoomCall(args));
  },
});

const mapStateToProps = (state: StoreState) => ({
  client: state.clientDetailState.user,
  liveTrainingRemainingSessions:
    state.clientDetailState.liveTrainingRemainingSessions,
  isTrainer: state.authenticatedUserState.user?.type === UserType.Trainer,
});

export default connect(mapStateToProps, mapDispatchToProps)(EditDialog);
